import { cn } from '@/lib/utils';
import { iconButtonClassName, navbarGhostIconButtonClassName, type IconButtonSize } from './button-variants';

export type IconButtonTone =
  'default' | 'table' | 'menu' | 'subtle' | 'back' | 'modalClose' | 'primary' | 'wizard' | 'inputAdornment';

export type IconButtonNavbarHitArea = 'compact' | 'comfortable' | 'responsive';

const toneClasses: Record<IconButtonTone, string | undefined> = {
  default: undefined,
  table:
    'text-muted-strong hover:text-foreground dark:hover:text-gray-200 disabled:text-muted disabled:hover:text-muted',
  menu: 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100',
  subtle: 'text-muted hover:text-foreground',
  back: 'text-neutral-900 hover:text-black dark:text-neutral-100 dark:hover:text-white',
  modalClose: 'ml-4 shrink-0 hover:bg-white/50 dark:hover:bg-gray-700/50',
  primary: 'text-primary dark:text-primary-400 hover:text-foreground',
  wizard:
    'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 dark:text-white/70 dark:hover:text-white dark:hover:bg-gray-700',
  inputAdornment:
    'h-8 w-8 shrink-0 text-muted hover:text-primary-500 dark:hover:text-primary-400 hover:translate-y-0 hover:shadow-none motion-reduce:hover:translate-y-0 active:scale-100 motion-reduce:active:scale-100',
};

const navbarHitAreaClasses: Record<IconButtonNavbarHitArea, string> = {
  compact: 'w-12 h-12 min-h-12 min-w-12 p-0',
  comfortable: 'p-4',
  responsive: 'w-12 h-12 min-h-12 min-w-12 md:h-10 md:w-10 md:min-h-10 md:min-w-10 p-0',
};

const tableCellClasses = cn(
  'h-10 w-10 sm:h-7 sm:w-7 touch-manipulation !rounded-md',
  'hover:!translate-y-0 hover:!shadow-none active:!translate-y-0 active:!shadow-sm',
  'motion-reduce:hover:!translate-y-0 disabled:hover:!translate-y-0 disabled:hover:!shadow-none',
  '[&_svg]:stroke-[2.25]',
);

export function resolveIconButtonToneClass(tone: IconButtonTone, destructive: boolean): string | undefined {
  if (tone === 'default' || (tone === 'table' && destructive)) return undefined;
  return toneClasses[tone];
}

export function resolveIconButtonClassName(args: {
  className?: string;
  size: IconButtonSize;
  navbar: boolean;
  navbarHitArea?: IconButtonNavbarHitArea;
  destructive: boolean;
  tone: IconButtonTone;
  tableCell: boolean;
  menuOpen: boolean;
  surface: 'default' | 'embedded';
  expandForLabel: boolean;
  pinned?: 'top-right';
}): string {
  return cn(
    args.navbar ? navbarGhostIconButtonClassName : iconButtonClassName(args.size),
    args.navbar && 'relative',
    args.navbar && args.navbarHitArea && navbarHitAreaClasses[args.navbarHitArea],
    args.destructive &&
      !args.navbar &&
      'text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20',
    resolveIconButtonToneClass(args.tone, args.destructive),
    args.tableCell && tableCellClasses,
    args.tone === 'menu' &&
      args.menuOpen &&
      'bg-gray-100/90 dark:bg-gray-700/90 text-gray-900 dark:text-gray-100 shadow-sm',
    args.surface === 'embedded' && 'border-0 bg-transparent',
    args.expandForLabel && 'inline-flex items-center gap-2 px-3 w-auto min-w-0 h-10',
    args.pinned === 'top-right' && 'absolute top-2 right-2 z-10',
    args.className,
  );
}
